import { Component, type ErrorInfo, type ReactNode } from 'react'
import { withTranslation, type WithTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'

/**
 * Story E01.12 — root + per-feature error boundaries.
 *
 * React still requires `componentDidCatch` to live in a class component
 * (no hook equivalent in 19.x). The class is intentionally thin: capture
 * the error to Sentry, remember the event-id (so the fallback UI can
 * surface a "support id"), and re-render a translated fallback.
 *
 * `withTranslation` (HOC) is used instead of `useTranslation` because the
 * latter is hook-only and class components can't consume hooks. Bundle
 * cost is negligible (re-uses the same i18next instance).
 *
 * AC-01 / AC-02 — root boundary mounted in `main.tsx`.
 * AC-03      — `FeatureErrorBoundary` (below) accepts a `featureName`
 *              prop so per-feature islands in E5+ can isolate failures
 *              without bringing the whole app down.
 */

type Props = WithTranslation & {
  children: ReactNode
  /**
   * Optional human-readable feature name. Forwarded to Sentry as a tag so
   * triage knows which surface broke. Omitted at the root (errors there
   * are by definition app-wide).
   */
  featureName?: string
  /** Custom fallback override. Defaults to `<DefaultFallback />`. */
  fallback?: (errorId: string | undefined, retry: () => void) => ReactNode
}

type State = {
  hasError: boolean
  errorId: string | undefined
}

class ErrorBoundaryInner extends Component<Props, State> {
  state: State = { hasError: false, errorId: undefined }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Capture the failure with extra context for triage. We let Sentry
    // assign the event-id (returned synchronously) and stash it in state
    // so the fallback UI can echo it back to the user as a support code.
    const eventId = Sentry.captureException(error, {
      tags: this.props.featureName ? { feature: this.props.featureName } : undefined,
      contexts: {
        react: { componentStack: info.componentStack ?? 'unknown' },
      },
    })
    this.setState({ errorId: eventId })
  }

  private handleRetry = (): void => {
    // Two layers of recovery:
    //  1. Reset boundary-local state so React tries to re-render children.
    //  2. If the underlying defect is module-level (broken chunk, stale
    //     cache), the user can fall back to a hard reload via the button
    //     wired in DefaultFallback.
    this.setState({ hasError: false, errorId: undefined })
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children
    if (this.props.fallback) return this.props.fallback(this.state.errorId, this.handleRetry)
    return <DefaultFallback errorId={this.state.errorId} t={this.props.t} />
  }
}

/**
 * Visual fallback. Kept inline (rather than a separate file) because it
 * is private to this module — nothing else should render the "Щось пішло
 * не так" surface.
 */
function DefaultFallback({ errorId, t }: { errorId: string | undefined; t: WithTranslation['t'] }) {
  return (
    <div
      role="alert"
      className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center"
    >
      <h1 className="text-2xl font-semibold">{t('errors.something_wrong')}</h1>
      {errorId ? (
        <p className="text-sm text-muted-foreground">{t('errors.support_id', { id: errorId })}</p>
      ) : null}
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-md border border-border bg-bg px-4 py-2 text-sm font-medium hover:bg-accent"
      >
        {t('errors.reload')}
      </button>
    </div>
  )
}

/**
 * Root error boundary — wraps `<App />` in `main.tsx`. Default export so
 * the `withTranslation` HOC can ship the i18n-bound version cleanly.
 */
export const ErrorBoundary = withTranslation()(ErrorBoundaryInner)

/**
 * Per-feature error boundary. Same component under the hood — the only
 * difference is that callers MUST pass a `featureName` so Sentry can
 * tag the event for triage. Use this around lazy feature shells in E5+
 * (e.g. `<FeatureErrorBoundary featureName="projects">…</FeatureErrorBoundary>`).
 */
export function FeatureErrorBoundary({
  featureName,
  children,
}: {
  featureName: string
  children: ReactNode
}) {
  return <ErrorBoundary featureName={featureName}>{children}</ErrorBoundary>
}
